import {
  formatMembershipProfileValue,
  type SurveyParticipantSnapshot,
} from '@citizen-walk/shared-types';

/** 回答に記録する回答者情報のうち、ニックネーム以外 (参加登録のプロフィールと参加セッション)。 */
export type SurveyParticipantProfile = Omit<SurveyParticipantSnapshot, 'nickname'>;

export const EMPTY_SURVEY_PARTICIPANT_PROFILE: SurveyParticipantProfile = {
  ageGroup: null,
  gender: null,
  relationship: null,
  attribute: null,
  joinedSessions: [],
};

/**
 * 参加登録 (membership) の値から回答者情報を組み立てる (純粋関数)。
 *
 * 旧 `old/apps/user/src/lib/surveys.ts` の `buildParticipantSnapshot` を踏襲: 回答のたびに
 * membership のプロフィール 4 項目と参加セッション (名前・コード) を焼き込む。v2 では
 * 送信が outbox 経由で getDoc を挟めないため、画面側で読み込み済みの値から組み立てる。
 * 専用欄は複数選択・数値等を持ちうる (MembershipProfileValue) ので文字にそろえ、空は null。
 * 地区から消えたセッションは旧と同じく id を名前の代わりに残す。
 */
export function buildSurveyParticipantProfile(args: {
  builtin: {
    gender: unknown;
    ageGroup: unknown;
    relationship: unknown;
    attribute: unknown;
  };
  joinedSessionIds: string[];
  sessions: Array<{ id: string; name: string; code: string }>;
}): SurveyParticipantProfile {
  const toText = (v: unknown): string | null => formatMembershipProfileValue(v) || null;
  const byId = new Map(args.sessions.map((s) => [s.id, s]));
  return {
    ageGroup: toText(args.builtin.ageGroup),
    gender: toText(args.builtin.gender),
    relationship: toText(args.builtin.relationship),
    attribute: toText(args.builtin.attribute),
    joinedSessions: args.joinedSessionIds.map((sid) => {
      const s = byId.get(sid);
      return { sessionId: sid, name: s?.name ?? sid, code: s?.code ?? '' };
    }),
  };
}
